import { z } from 'zod';

import { type InsuranceOrderResponse, type Order } from '~/types/order';

/** Slot or discovered row is marked as submitted on the referral (supports legacy patient_submitted). */
export function isReferralSubmittedInsurance(insurance: InsuranceOrderResponse): boolean {
  return insurance.referralSubmitted === true || insurance.patientSubmitted === true;
}

export type PatientInsurance = {
  insuranceId: string;
  memberId: string;
  groupNumber: string;
  name: string;
  planCode: string;
  state: string;
};

export const CARRIER_TYPE = {
  MEDICARE: 'medicare',
  MEDICARE_ADVANTAGE: 'medicare_advantage',
  MEDICAID: 'medicaid',
  MEDICAID_MANAGED_CARE: 'medicaid_managed_care',
} as const;

export const CarrierTypeSchema = z.enum([
  'medicare',
  'medicare_advantage',
  'medicaid',
  'medicaid_managed_care',
]);

export type CarrierType = z.infer<typeof CarrierTypeSchema>;

export const InsuranceOptionSchema = z.object({
  uuid: z.uuid(),
  frhcId: z.number().optional(),
  displayName: z.string(),
  name: z.string(),
  state: z.string(),
  carrierType: CarrierTypeSchema.optional(),
  carrierTypes: z.array(CarrierTypeSchema).nullish(),
  templateUuid: z.string().optional(),
  isOther: z.boolean().default(false),
});

export type InsuranceOption = z.infer<typeof InsuranceOptionSchema>;

export function groupInsuranceOptionsByCarrierType(
  options: InsuranceOption[]
): Map<CarrierType, InsuranceOption[]> {
  return options.reduce((acc, option) => {
    if (!option.carrierType) return acc;

    const current = acc.get(option.carrierType) ?? [];
    acc.set(option.carrierType, [...current, option]);
    return acc;
  }, new Map<CarrierType, InsuranceOption[]>());
}

export const MultiStateInsuranceOptionSchema = z.object({
  uuid: z.uuid(),
  displayName: z.string(),
  name: z.string(),
  state: z.string(),
});

type CarrierName = 'Medicare' | 'Medicaid' | 'Private';

export function getCarrierName(order: Order): CarrierName {
  return getCarrierNameFromInsurance(order.insurance);
}

export function getCarrierNameFromInsurance(insurance: InsuranceOrderResponse): CarrierName {
  const carrierType = insurance.carrierType ?? insurance.name;
  return transformCarrierName(carrierType);
}

function transformCarrierName(name: string | undefined): CarrierName {
  const lower = name?.toLowerCase();
  if (lower === 'medicare' || lower === 'medicare_advantage') return 'Medicare';
  return 'Medicaid';
}
